import { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';
import { downloadFile, useRestoreFile } from '@/api/files';
import { useNavigate } from 'react-router-dom';
import type { FileItem } from '@/types/api';
import { useUIStore } from '@/stores/ui';
import { apiError } from '@/lib/api';
import { RenameFileModal } from './RenameFileModal';
import { MoveFileModal } from './MoveFileModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';

interface Props {
  file: FileItem;
  workspaceId?: string;
  canManage?: boolean;
}

export function FileActions({ file, workspaceId, canManage = false }: Props) {
  const [modal, setModal] = useState<'rename' | 'move' | 'delete' | null>(null);
  const restore = useRestoreFile();
  const navigate = useNavigate();
  const push = useUIStore((s) => s.pushToast);

  const handleDownload = async () => {
    try {
      await downloadFile(file.id, file.name);
    } catch (e) {
      push({ title: 'Download failed', body: apiError(e), variant: 'error' });
    }
  };

  return (
    <>
      <Dropdown
        align="right"
        trigger={
          <button
            className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700 transition-colors"
            aria-label="File actions"
          >
            <MoreVertical size={16} />
          </button>
        }
      >
        <DropdownItem onClick={() => navigate(`/files/${file.id}`)}>Info</DropdownItem>
        <DropdownItem onClick={handleDownload}>Download</DropdownItem>
        {canManage && !file.is_deleted && (
          <>
            <DropdownItem onClick={() => setModal('rename')}>Rename</DropdownItem>
            {workspaceId && (
              <DropdownItem onClick={() => setModal('move')}>Move to folder</DropdownItem>
            )}
            <DropdownItem danger onClick={() => setModal('delete')}>
              Delete
            </DropdownItem>
          </>
        )}
        {canManage && file.is_deleted && (
          <DropdownItem
            disabled={restore.isPending}
            onClick={() =>
              restore.mutate(
                { id: file.id, folder_id: file.folder_id },
                {
                  onSuccess: () => push({ title: 'File restored', variant: 'success' }),
                  onError: (e) =>
                    push({ title: 'Restore failed', body: apiError(e), variant: 'error' }),
                },
              )
            }
          >
            Restore
          </DropdownItem>
        )}
      </Dropdown>

      <RenameFileModal
        open={modal === 'rename'}
        onClose={() => setModal(null)}
        file={file}
      />

      {workspaceId && (
        <MoveFileModal
          open={modal === 'move'}
          onClose={() => setModal(null)}
          file={file}
          workspaceId={workspaceId}
        />
      )}

      <DeleteConfirmModal
        open={modal === 'delete'}
        onClose={() => setModal(null)}
        file={file}
      />
    </>
  );
}
